// WHAT: Component for displaying active Miracle Flow session status
// WHY:  Provides visual indicator of active session state with auto-refresh
// DOMAIN: Core → Dashboard
// DEPENDENCIES: sessionApi from Miracle Flow
// RISKS:
//   - NOTE: Auto-refresh interval (30s) may not be optimal for all use cases
//   - NOTE: Error handling is silent - may not provide user feedback
//   - NOTE: Loading state may flash on mount

import { type FC, useEffect, useState } from "react";

import { Card, Heading, Text } from "@/components/ui";
import { sessionApi } from "@/domains/productivity/miracle-flow/utils/api-client";

/**
 * Props for SessionStatusIndicator component
 */
interface SessionStatusIndicatorProperties {
  /** Additional CSS classes */
  className?: string;
}

/**
 * WHAT:
 *   Component that displays active Miracle Flow session status with
 *   automatic refresh and visual indicators.
 *
 * WHY:
 *   Enables users to see at a glance whether a Miracle Flow session is
 *   currently active.
 *
 * BEHAVIOR:
 *   - Checks for active session on mount
 *   - Auto-refreshes every 30 seconds
 *   - Shows loading state during check
 *   - Displays active/inactive status with color coding
 *
 * RISKS:
 *   - NOTE: Auto-refresh interval (30s) may not be optimal for all use cases
 *   - NOTE: Error handling is silent - may not provide user feedback
 *   - NOTE: Loading state may flash on mount
 *
 * @param root0
 *
 * @param root0.className
 *
 * @returns Session status indicator component
 *
 */
export const SessionStatusIndicator: FC<SessionStatusIndicatorProperties> = ({ className }) => {
  const [hasActiveSession, setHasActiveSession] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        setIsLoading(true);
        const active = await sessionApi.hasActiveSession();
        setHasActiveSession(active);
      } catch {
        // NOTE: Error checking active session - default to false
        setHasActiveSession(false);
      } finally {
        setIsLoading(false);
      }
    };

    void checkActiveSession();

    // NOTE: Check every 30 seconds for updates
    const interval = setInterval(() => {
      void checkActiveSession();
    }, 30_000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  if (isLoading) {
    return (
      <Card {...(className ? { className } : {})}>
        <div className="space-y-3">
          <Heading level={4}>Session Status</Heading>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
            <Text className="text-gray-400" size="sm">
              Checking...
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card {...(className !== undefined && { className })}>
      <div className="space-y-3">
        <Heading level={4}>Session Status</Heading>
        <div className="flex items-center space-x-2">
          <div
            className={`w-3 h-3 rounded-full ${
              hasActiveSession ? "bg-green-500 animate-pulse" : "bg-gray-400"
            }`}
          />
          <Text
            className={hasActiveSession ? "text-green-400 font-medium" : "text-gray-400"}
            size="sm"
          >
            {hasActiveSession ? "Active Session" : "No Active Session"}
          </Text>
        </div>
        {hasActiveSession ? (
          <Text className="text-gray-500" size="sm">
            MiracleFlow session in progress
          </Text>
        ) : null}
      </div>
    </Card>
  );
};
